import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { AssignmentDetails, Pagination, ReviewAssignmentDto, ReviewOutcome, ReviewSubjectType, UpdateReviewOutcomeRequest } from '../types';
import { of, retry, switchMap, throwError, timer } from 'rxjs';

export interface GetQueuedHistoryRequest {
  sources?: string[];
  startUtc?: string;
  endUtc?: string;
  page?: number;
  items_per_page?: number;
}

export interface QueuedHistoryAssignment {
  review_id: string;
  review_subject?: ReviewSubjectType;
  branch_id?: number;
  branch_name?: string;
  location_id?: number;
  location_name?: string;
  theft_plan?: string;
}

export interface QueuedHistoryRun {
  source: string;
  run_start_utc: string;
  assignments: QueuedHistoryAssignment[];
}

@Injectable({ providedIn: 'root' })
export class AssignmentService {
  apiHost = `${environment.apiBaseUrl}`;

  private httpClient = inject(HttpClient);

  createAssignment(location_id: number, review_message: string, review_subject_type: ReviewSubjectType, add_to_queue: boolean) {
    const requestUrl = `${this.apiHost}/Assignments`;
    return this.httpClient.post<ReviewAssignmentDto>(requestUrl, {
      location_id,
      notes: review_message,
      review_subject_type,
      add_to_queue,
    });
  }

  /**
   * Get the next assignment for the current user.
   *
   * Reminder:
   *  this API is a MUTATION.
   *  if backend finds available assignment for user, it will assign it (mutation).
   */
  getNextAssignment(locationId?: number) {
    const requestUrl = `${this.apiHost}/Assignments/Next`;
    let params = new HttpParams();
    if (locationId) params = params.set('location_id', locationId.toString());
    return this.httpClient.get<AssignmentDetails>(requestUrl, { params });
  }

  getAssignment(reviewId: string, options?: { skipAssignment?: boolean }) {
    const requestUrl = `${this.apiHost}/Assignments/${reviewId}`;
    let params = new HttpParams();
    if (options?.skipAssignment) params = params.set('skip_assignment', 'true');
    return this.httpClient.get<AssignmentDetails>(requestUrl, { params }).pipe(
      switchMap((result) => (result ? of(result) : throwError(() => new Error('No assignments available')))),
      retry({ count: 3, delay: () => timer(500 + Math.random() * 500) }),
    );
  }

  getQueuedHistory({ sources, startUtc, endUtc, page, items_per_page }: GetQueuedHistoryRequest = {}) {
    const url = `${this.apiHost}/Assignments/QueuedHistory`;
    let params = new HttpParams();
    if (sources && sources.length > 0) params = params.set('sources', sources.join(','));
    if (startUtc) params = params.set('start_utc', startUtc);
    if (endUtc) params = params.set('end_utc', endUtc);
    if (page !== undefined) params = params.set('page', page.toString());
    if (items_per_page !== undefined) params = params.set('items_per_page', items_per_page.toString());
    return this.httpClient.get<Pagination<QueuedHistoryRun>>(url, { params });
  }

  getReviewOutcomes(reviewId: string) {
    const requestUrl = `${this.apiHost}/Assignments/${reviewId}/ReviewOutcomes`;
    return this.httpClient.get<ReviewOutcome[]>(requestUrl);
  }

  updateReviewOutcome(reviewId: string, reviewOutcomeId: string, request: UpdateReviewOutcomeRequest) {
    const requestUrl = `${this.apiHost}/Assignments/${reviewId}/ReviewOutcomes/${reviewOutcomeId}`;
    return this.httpClient.put<void>(requestUrl, request);
  }
}
